import type { JoustingView } from '@wyn/contracts';
import { TICK_MS } from '@wyn/game-core';
import { useEffect, useRef, useState } from 'react';
import { Callout } from '../components/ui.tsx';
import { Track } from './parts.tsx';
import type { TurnGameProps } from './registry.tsx';

type Aim = 'helm' | 'chest' | 'shield';
type Guard = 'high' | 'low';

/**
 * The Lists. Read the tells, choose a target and a guard, then charge: the lance is couched at the
 * tick you press, and the server judges that tick (the foe's real aim and guard never leave it).
 */
export function JoustingGame({ view, busy, onMove }: TurnGameProps<JoustingView>) {
  const [aim, setAim] = useState<Aim | null>(null);
  const [guard, setGuard] = useState<Guard | null>(null);
  const [charging, setCharging] = useState(false);

  // A new pass: choose afresh.
  useEffect(() => {
    setAim(null);
    setGuard(null);
    setCharging(false);
  }, [view.pass, view.over]);
  // A charge the server turned down: let the player choose and charge again.
  const wasBusy = useRef(busy);
  useEffect(() => {
    if (wasBusy.current && !busy) setCharging(false);
    wasBusy.current = busy;
  }, [busy]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-ink-dim">
        Helm is worth 3 with a narrow timing window, Chest 2, Shield 1 but never blocked. A <b>high</b> guard stops helm blows, a <b>low</b> guard stops chest blows. A perfect unblocked helm strike unhorses him {Math.round(view.unhorse * 100)}% of the time, and once his helm has been struck he guards it more.
      </p>
      <div className="flex justify-between font-mono">
        <span>You <b>{view.me}</b></span>
        <span>Pass {view.pass}/{view.passes}</span>
        <span>{view.foeName} <b>{view.foe}</b></span>
      </div>
      {view.last && <Callout tone="gold" role="status">{view.last.text}</Callout>}
      {!view.over && !charging && (
        <>
          <p className="text-center italic">{view.tells}</p>
          <div className="space-y-1">
            <div className="smallcaps text-sm text-ink-dim">Strike at</div>
            <div className="grid grid-cols-3 gap-2">
              {(Object.keys(view.aims) as Aim[]).map((k) => (
                <button key={k} type="button" aria-pressed={aim === k} className={`btn ${aim === k ? 'bg-gilt text-vellum' : ''}`} onClick={() => setAim(k)}>
                  {view.aims[k].label} ({view.aims[k].points})
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1">
            <div className="smallcaps text-sm text-ink-dim">Guard</div>
            <div className="grid grid-cols-2 gap-2">
              {(['high', 'low'] as const).map((g) => (
                <button key={g} type="button" aria-pressed={guard === g} className={`btn ${guard === g ? 'bg-gilt text-vellum' : ''}`} onClick={() => setGuard(g)}>
                  {g === 'high' ? 'High guard' : 'Low guard'}
                </button>
              ))}
            </div>
          </div>
          <button type="button" className="btn w-full py-3" disabled={busy || !aim || !guard} onClick={() => setCharging(true)}>
            Charge!
          </button>
        </>
      )}
      {!view.over && charging && aim && guard && (
        <Charge view={view} aim={aim} busy={busy} onCouch={(tick) => onMove({ type: 'charge', aim, guard, tick })} />
      )}
    </div>
  );
}

/** The charge down the list: a marker sweeps towards the meeting point; press as it crosses. */
function Charge({ view, aim, busy, onCouch }: { view: JoustingView; aim: Aim; busy: boolean; onCouch: (tick: number) => void }) {
  const [pos, setPos] = useState(0);
  const start = useRef(performance.now());
  const sent = useRef(false);
  const couch = () => {
    if (sent.current) return;
    sent.current = true;
    onCouch(Math.floor((performance.now() - start.current) / TICK_MS));
  };
  const couchRef = useRef(couch);
  couchRef.current = couch;

  useEffect(() => {
    let raf = 0;
    const tick = (now: number) => {
      const p = (now - start.current) / 1000 / view.duration;
      setPos(Math.min(1, p));
      if (p >= 1) return couchRef.current();
      if (!sent.current) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const key = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat) return;
      e.preventDefault();
      couchRef.current();
    };
    addEventListener('keydown', key);
    return () => {
      cancelAnimationFrame(raf);
      removeEventListener('keydown', key);
    };
  }, [view.duration]);

  const w = view.aims[aim].window, z = view.zone;
  return (
    <div className="space-y-3">
      <div className="relative h-20 overflow-hidden border border-rule bg-gradient-to-b from-[#3b4a2c] to-scene-grass">
        <div className="absolute inset-x-0 top-[38px] h-1 bg-[#8a6a3a]" />
        <div className="absolute top-2 text-4xl" style={{ left: `calc(${pos * 48}% - 10px)` }}>🏇</div>
        <div className="absolute top-9 -scale-x-100 text-4xl" style={{ right: `calc(${pos * 48}% - 10px)` }}>🏇</div>
      </div>
      <Track
        value={pos * 100}
        label="Lance"
        zones={[
          { from: (z - w) * 100, to: (z + w) * 100, className: 'bg-gilt/30' },
          { from: (z - w / 2) * 100, to: (z + w / 2) * 100, className: 'bg-verdigris/60' },
        ]}
      />
      <div className="flex justify-between text-xs text-ink-faint"><span>Lance</span><span>{view.aims[aim].label}</span></div>
      <button type="button" className="w-full border-2 border-gilt/70 py-4 text-lg text-gilt" disabled={busy} onPointerDown={(e) => { e.preventDefault(); couch(); }}>
        Couch lance!
      </button>
    </div>
  );
}
